import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import DeleteForeverRoundedIcon from '@material-ui/icons/DeleteForeverRounded';
import moment from 'moment';

import HealtheraTable from '../../Table/Table';
import { cutLongText } from '../../../helpers';
import { coreApi } from '../../../utils/axios';

function LoadingCell({ height = 60 }) {
  return (
    <Cell height={height}>
      <LoadingText />
    </Cell>
  );
};

const headers = {
  view: {
    title: { title: 'Title', width: '20%' },
    description: { title: 'Description' },
    created: { title: 'Date Created', width: '10%' },
    actions: { title: 'Actions', width: '10%' }
  },
  headers: true
};

const prepareLoadingData = limit => {
  return Array.from({ length: limit }).reduce(
    data => {
      data.push({
        view: {
          title: <LoadingCell />,
          description: <LoadingCell />,
          created: <LoadingCell />,
          actions: <LoadingCell />,
        }
      });
      return data;
    },
    [headers]
  );
};

const prepareData = (announcements, deleteAnnouncement) => {
  if (!announcements?.length) {
    return [];
  }

  return announcements.reduce(
    (data, announcement) => {
      const { _id, title, description, created } = announcement;

      data.push({
        view: {
          title,
          description: <ServiceDescription>{cutLongText(description, 80)}</ServiceDescription>,
          created: moment(created).format('DD/MM/YYYY'),
          actions: (<UnstyledButton title="Delete announcement" onClick={() => deleteAnnouncement(_id)}>
            <DeleteForeverRoundedIcon />
          </UnstyledButton>)
        },
      });

      return data;
    },
    [headers]
  );
};

const AnnouncementsTable = () => {
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [announcements, setAnnouncements] = useState([]);

  const fetchAnnouncements = useCallback(async () => {
    setLoading(true);
      const response = await coreApi.get(`/announcements?page=${page}&limit=${limit}`);
      if (response.data?.data) {
        setAnnouncements(response.data?.data);
        setTotal(response.data.pagination.total);
      }
      setLoading(false);
  }, [page, limit]);

  useEffect(() => {
    fetchAnnouncements();
  }, [fetchAnnouncements]);

  const deleteAnnouncement = useCallback(async (_id) => {
    if (_id) {
      await coreApi.delete(`/announcements/${_id}`);
      fetchAnnouncements();
    }
  }, [fetchAnnouncements]);

  const preparedData = loading ? prepareLoadingData(limit) : prepareData(announcements, deleteAnnouncement);

  return (
    <>
      {preparedData?.length > 0 ? (
        <HealtheraTable
          data={preparedData}
          muted
          totalCount={total}
          rowsPerPage={limit}
          rowsPerPageOptions={[10, 15, 20]}
          onChangePage={(e) => setPage(e.target.value)}
          onRowsPerPageChange={(e) => setLimit(e.target.value)}
          page={page}
          displayPagination
        />
      ) : (<h1>No announcements yet!</h1>)}
    </>
  );
};

const ServiceDescription = styled.p`
  padding: 10px 0;
`;

const Cell = styled.div`
  ${props => `height: ${props.height}px`};
  display: flex;
  align-items: center;
`;

const loadAnimation = keyframes`
  from {
    opacity: 0.3;
  }

  to {
    opacity: 0.8;
  }
`;

const LoadingText = styled.p`
  margin: 0;
  animation: ${loadAnimation} 0.7s linear alternate infinite;
  width: 100%;
  height: 16px;
  background-color: rgba(200, 200, 200, 0.8);
  border-radius: 5px;
`;

const UnstyledButton = styled.button`
  border: 0;
  background-color: transparent;
  cursor: pointer;
`;

export default AnnouncementsTable;
